import { ColDef } from "ag-grid-community";
import { Column } from "constants/columns";
import { ParentSaleTabs, ParentSaleTabType } from "constants/sale";

import { BaseColumnDefinitions } from "lib/agGrid/columnDefinitions";

const COMMERCIAL_BUYER_COLUMNS: Array<ColDef<SaleLot>> = [
  BaseColumnDefinitions[Column.AGENCY_NAME],
  BaseColumnDefinitions[Column.QUANTITY_DELIVERED],
  BaseColumnDefinitions[Column.PEN],
  BaseColumnDefinitions[Column.BUYER_NAME],
  BaseColumnDefinitions[Column.BUYER_WAY_NAME],
  BaseColumnDefinitions[Column.DESTINATION_PIC],
  BaseColumnDefinitions[Column.NVD],
  BaseColumnDefinitions[Column.VENDOR_NUM],
  BaseColumnDefinitions[Column.VENDOR_PIC],
  BaseColumnDefinitions[Column.TOTAL_PRICE],
  BaseColumnDefinitions[Column.WEIGHT],
  BaseColumnDefinitions[Column.AVERAGE_WEIGHT],
  BaseColumnDefinitions[Column.LOT_NUMBER_COMBINED],
  BaseColumnDefinitions[Column.UNIT_PRICE],
  BaseColumnDefinitions[Column.QUANTITY],
  BaseColumnDefinitions[Column.DIFF],
  BaseColumnDefinitions[Column.BREED_NAME],
  BaseColumnDefinitions[Column.SEX_NAME],
  BaseColumnDefinitions[Column.AGE_GROUP_NAME],
  BaseColumnDefinitions[Column.CATEGORY_NAME],
  BaseColumnDefinitions[Column.GRADE_NAME],
  BaseColumnDefinitions[Column.SALE_ROUND_NAME],
  BaseColumnDefinitions[Column.STATUS],
  BaseColumnDefinitions[Column.THIRD_PARTY_NAME],
  {
    // We don't want the edit business popup being linked off here - just the name of the business.
    ...BaseColumnDefinitions[Column.INVOICE_TO_NAME],
    cellRenderer: null,
  },
];

const COMPLIANCE_BUYER_COLUMNS: Array<ColDef<SaleLot>> = [
  BaseColumnDefinitions[Column.AGENCY_NAME],
  BaseColumnDefinitions[Column.QUANTITY_DELIVERED],
  BaseColumnDefinitions[Column.PEN],
  BaseColumnDefinitions[Column.VENDOR_NAME],
  BaseColumnDefinitions[Column.BUYER_NAME],
  BaseColumnDefinitions[Column.BUYER_WAY_NAME],
  BaseColumnDefinitions[Column.BUYER_SHORT_CODE],
  BaseColumnDefinitions[Column.BUYER_SHORT_CODE_SALEYARD],
  BaseColumnDefinitions[Column.DESTINATION_PIC],
  BaseColumnDefinitions[Column.NVD],
  BaseColumnDefinitions[Column.VENDOR_NUM],
  BaseColumnDefinitions[Column.VENDOR_PIC],
  BaseColumnDefinitions[Column.WEIGHT],
  BaseColumnDefinitions[Column.AVERAGE_WEIGHT],
  BaseColumnDefinitions[Column.LOT_NUMBER_COMBINED],
  BaseColumnDefinitions[Column.SCANNED_COUNT],
  BaseColumnDefinitions[Column.SCANNED_PERCENTAGE],
  BaseColumnDefinitions[Column.QUANTITY],
  BaseColumnDefinitions[Column.DIFF],
  BaseColumnDefinitions[Column.BREED_NAME],
  BaseColumnDefinitions[Column.SEX_NAME],
  BaseColumnDefinitions[Column.AGE_GROUP_NAME],
  BaseColumnDefinitions[Column.CATEGORY_NAME],
  BaseColumnDefinitions[Column.GRADE_NAME],
  BaseColumnDefinitions[Column.SALE_ROUND_NAME],
  BaseColumnDefinitions[Column.STATUS],
  BaseColumnDefinitions[Column.THIRD_PARTY_NAME],
  {
    // We don't want the edit business popup being linked off here - just the name of the business.
    ...BaseColumnDefinitions[Column.INVOICE_TO_NAME],
    cellRenderer: null,
  },
];

const COMMERCIAL_VENDOR_COLUMNS: Array<ColDef<SaleLot>> = [
  BaseColumnDefinitions[Column.AGENCY_NAME],
  BaseColumnDefinitions[Column.QUANTITY_DELIVERED],
  BaseColumnDefinitions[Column.PEN],
  BaseColumnDefinitions[Column.BUYER_WAY_NAME],
  BaseColumnDefinitions[Column.DESTINATION_PIC],
  BaseColumnDefinitions[Column.NVD],
  BaseColumnDefinitions[Column.VENDOR_NUM],
  BaseColumnDefinitions[Column.VENDOR_PIC],
  BaseColumnDefinitions[Column.TOTAL_PRICE],
  BaseColumnDefinitions[Column.WEIGHT],
  BaseColumnDefinitions[Column.AVERAGE_WEIGHT],
  BaseColumnDefinitions[Column.LOT_NUMBER_COMBINED],
  BaseColumnDefinitions[Column.UNIT_PRICE],
  BaseColumnDefinitions[Column.QUANTITY],
  BaseColumnDefinitions[Column.DIFF],
  BaseColumnDefinitions[Column.BREED_NAME],
  BaseColumnDefinitions[Column.SEX_NAME],
  BaseColumnDefinitions[Column.AGE_GROUP_NAME],
  BaseColumnDefinitions[Column.CATEGORY_NAME],
  BaseColumnDefinitions[Column.GRADE_NAME],
  BaseColumnDefinitions[Column.SALE_ROUND_NAME],
  BaseColumnDefinitions[Column.STATUS],
];

const COMPLIANCE_VENDOR_COLUMNS: Array<ColDef<SaleLot>> = [
  BaseColumnDefinitions[Column.AGENCY_NAME],
  BaseColumnDefinitions[Column.QUANTITY_DELIVERED],
  BaseColumnDefinitions[Column.PEN],
  BaseColumnDefinitions[Column.VENDOR_NAME],
  BaseColumnDefinitions[Column.BUYER_WAY_NAME],
  BaseColumnDefinitions[Column.BUYER_SHORT_CODE],
  BaseColumnDefinitions[Column.BUYER_SHORT_CODE_SALEYARD],
  BaseColumnDefinitions[Column.DESTINATION_PIC],
  BaseColumnDefinitions[Column.NVD],
  BaseColumnDefinitions[Column.VENDOR_NUM],
  BaseColumnDefinitions[Column.VENDOR_PIC],
  BaseColumnDefinitions[Column.WEIGHT],
  BaseColumnDefinitions[Column.AVERAGE_WEIGHT],
  BaseColumnDefinitions[Column.LOT_NUMBER_COMBINED],
  BaseColumnDefinitions[Column.SCANNED_COUNT],
  BaseColumnDefinitions[Column.SCANNED_PERCENTAGE],
  BaseColumnDefinitions[Column.QUANTITY],
  BaseColumnDefinitions[Column.DIFF],
  BaseColumnDefinitions[Column.BREED_NAME],
  BaseColumnDefinitions[Column.SEX_NAME],
  BaseColumnDefinitions[Column.AGE_GROUP_NAME],
  BaseColumnDefinitions[Column.CATEGORY_NAME],
  BaseColumnDefinitions[Column.GRADE_NAME],
  BaseColumnDefinitions[Column.SALE_ROUND_NAME],
  BaseColumnDefinitions[Column.STATUS],
];

const BUSINESS_USER_PARENT_TAB_COMMERCIAL_COLUMNS: Record<
  ParentSaleTabType,
  Array<ColDef>
> = {
  [ParentSaleTabs.BOUGHT]: COMMERCIAL_BUYER_COLUMNS,
  [ParentSaleTabs.SOLD]: COMMERCIAL_VENDOR_COLUMNS,
};

const BUSINESS_USER_PARENT_TAB_COMPLIANCE_COLUMNS: Record<
  ParentSaleTabType,
  Array<ColDef>
> = {
  [ParentSaleTabs.BOUGHT]: COMPLIANCE_BUYER_COLUMNS,
  [ParentSaleTabs.SOLD]: COMPLIANCE_VENDOR_COLUMNS,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Empty array placeholder for future column definitions
const OTHER_USER_COLUMNS: Array<any> = [];

interface GetBusinessUserSaleReportsColumnsFunction {
  (
    isCommercialUser: boolean,
    isComplianceUser: boolean,
    parentSaleTab: ParentSaleTabType,
  ): Array<ColDef>;
}

export const getBusinessUserSaleReportsColumns: GetBusinessUserSaleReportsColumnsFunction =
  (isCommercialUser, isComplianceUser, parentSaleTab) => {
    if (isCommercialUser) {
      return BUSINESS_USER_PARENT_TAB_COMMERCIAL_COLUMNS[parentSaleTab] || [];
    }
    if (isComplianceUser) {
      return BUSINESS_USER_PARENT_TAB_COMPLIANCE_COLUMNS[parentSaleTab] || [];
    }
    return OTHER_USER_COLUMNS;
  };
